import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrenciesStore } from '@app/ui/stores';
import { ReportType, type ReportEntity } from '@app/domain/entities';
import { transformReportAmounts } from '../report-viewer.utils';
import {
  loadExcelJs,
  sanitizeSheetName,
  sanitizeFilename,
  currencyNumFmt,
  addFormattedRow,
  setColumnWidths,
} from './report-export.utils';
import { REPORT_EXPORT_FILE_SERVICE, type ReportExportFileService } from './report-export-file.service';
import type { IReportExportUseCase } from './report-export.use-case';

interface ExpensesIncomesItem {
  account?: string;
  category?: string;
  subcategory?: string;
  amounts: Record<string, number>;
}

interface ExpensesIncomesReportData {
  options: {
    includeAccounts: boolean;
    includeCategories: boolean;
    includeSubcategories: boolean;
  };
  filters: {
    dateFrom: Date | undefined;
    dateTo: Date | undefined;
  };
  data: {
    incomes: ExpensesIncomesItem[];
    expenses: ExpensesIncomesItem[];
    incomesTotals: Record<string, number>;
    expensesTotals: Record<string, number>;
    totals: Record<string, number>;
  };
}

@Injectable({ providedIn: 'root' })
export class ExportExpensesIncomesUseCase implements IReportExportUseCase {
  readonly reportType = ReportType.EXPENSES_AND_INCOMES;

  private readonly translate = inject(TranslateService);
  private readonly currenciesStore = inject(CurrenciesStore);
  private readonly reportExportFileService = inject<ReportExportFileService>(REPORT_EXPORT_FILE_SERVICE);

  async export(report: ReportEntity): Promise<void> {
    if (!report.data) return;

    const data = report.data as ExpensesIncomesReportData;
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();

    const currencies = this.currenciesStore.currencies();
    const { transformAmounts } = transformReportAmounts([...data.data.incomes, ...data.data.expenses], currencies);
    const allCurrencies = transformAmounts({}).map((a) => a.currency);

    const sections = [
      {
        title: this.translate.instant('screens.reports.labels.expenses') as string,
        items: data.data.expenses,
        totals: transformAmounts(data.data.expensesTotals),
      },
      {
        title: this.translate.instant('screens.reports.labels.incomes') as string,
        items: data.data.incomes,
        totals: transformAmounts(data.data.incomesTotals),
      },
    ];

    for (const section of sections) {
      const sheet = workbook.addWorksheet(sanitizeSheetName(section.title));

      const labelHeaders: string[] = [];
      if (data.options.includeAccounts) labelHeaders.push(this.translate.instant('common.labels.account'));
      if (data.options.includeCategories) labelHeaders.push(this.translate.instant('common.labels.category'));
      if (data.options.includeSubcategories) labelHeaders.push(this.translate.instant('common.labels.subcategory'));

      // Label columns have no number format; amount columns use per-currency format
      const labelCount = labelHeaders.length;
      const columnFormats: (string | undefined)[] = [
        ...Array<undefined>(labelCount).fill(undefined),
        ...allCurrencies.map((c) => currencyNumFmt(c.code)),
      ];

      sheet.addRow([...labelHeaders, ...allCurrencies.map((c) => c.name)]);
      setColumnWidths(sheet, [
        ...Array<'text'>(labelCount).fill('text'),
        ...allCurrencies.map(() => 'currency' as const),
      ]);

      for (const item of section.items) {
        const labelCells: (string | undefined)[] = [];
        if (data.options.includeAccounts) labelCells.push(item.account);
        if (data.options.includeCategories) {
          labelCells.push(
            item.category === '__opposite_side_adjustments__'
              ? this.translate.instant('screens.reports.labels.other_adjustments')
              : item.category,
          );
        }
        if (data.options.includeSubcategories) labelCells.push(item.subcategory);

        addFormattedRow(
          sheet,
          [...labelCells, ...transformAmounts(item.amounts).map((a) => a.value ?? 0)],
          columnFormats,
        );
      }

      if (section.totals.length > 0) {
        const totalLabel = this.translate.instant('screens.reports.labels.total') as string;
        const totalsValues: (string | number)[] = [
          ...Array.from({ length: labelCount }, (_, i) => (i === 0 ? totalLabel : '')),
          ...section.totals.map((a) => a.value ?? 0),
        ];
        addFormattedRow(sheet, totalsValues, columnFormats);
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const typeName = this.translate.instant(`screens.reports.types.${report.type.toLowerCase()}`) as string;
    await this.reportExportFileService.saveExcelFile(
      buffer,
      `${sanitizeFilename(typeName)}-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
}
